import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaExternalLinkAlt,
  FaSpinner,
} from "react-icons/fa";
import { ModalComponent, Pagination } from "../../components";
import BrokerAlert from "../../components/Broker/BrokerAlert";
import BrokerSkeleton from "../../components/Broker/BrokerSkeleton";
import FilterBar from "../../components/Broker/FilterBar";
import StatusBadge from "../../components/Broker/StatusBadge";
import BrokerAdminLabel from "../../components/Broker/BrokerAdminLabel";
import { buildBrokerPath, useBrokerBasePath } from "../../components/Broker/brokerPaths";
import { DialogContext } from "../../store/context/DialogContext";
import { colorForTag, normalizeTags } from "../../components/TagPicker";
import { BrokerService } from "./BrokerService";
import { brokerTagStore } from "./brokerTagStore";
import { getFormTypeLabel } from "../../util/applicationLabels";
import BrokerApplicationDetail from "./BrokerApplicationDetail";
import { BROKER_SYSTEM_TAG_FILTER_OPTIONS, getBrokerSystemTagId, getBrokerVisibleSystemTags, recordBrokerVisibleSystemTagElements } from "./brokerSystemTags";

const DEFAULT_ROWS_PER_PAGE = 40;
const DEFAULT_SORT = { sortBy: "updatedAt", sortAsc: false };

const DEFAULT_FILTERS = {
  status: "",
  // Hidden deep-link filter for dashboard categories that are narrower than the
  // four broker-facing status buckets, e.g. Missing Information (2,7) or Drafts (0).
  statusCodes: "",
  // Hidden deep-link filter — dashboard cards (KYC Failed/Due) link via kycStatus.
  kycStatus: "",
  // Hidden deep-link filter — "KYC Due" links via the KYC Verification step status
  // (e.g. "0,2"), filtered as steps.$elemMatch on the backend.
  kycStepStatus: "",
  // System-tag filter: MISSING_INFO / KYC_FAILED (resolved to a tag_ids query).
  systemTag: "",
  type: "",
  clientRef: "",
  applicationNo: "",
  clientName: "",
  clientEmail: "",
  brokerTags: "",
  createdFrom: "",
  createdTo: "",
};

// Simplified status groups for brokers — they don't need FV Bank's internal
// status codes, so we collapse them into four plain-language buckets.
const BROKER_STATUS_OPTIONS = [
  { value: "INCOMPLETE", label: "Incomplete" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "ONBOARDED", label: "Onboarded" },
  { value: "REJECTED", label: "Rejected" },
  { value: "DECLINED", label: "Declined" },
  // Not an application status: maps to the KYC Verification step being Draft (0) or
  // Incomplete (2), sent as the kycStepStatus grid filter (steps.$elemMatch).
  { value: "KYC_DUE", label: "KYC Due" },
];

// Visible broker filters (status is the always-on control rendered separately).
const BROKER_FILTER_DEFINITIONS = [
  { key: "clientRef", label: "Client Ref", control: "text", valueKey: "clientRef", placeholder: "Client reference", isActive: (f) => Boolean(f.clientRef) },
  { key: "applicationNo", label: "Application No.", control: "text", valueKey: "applicationNo", placeholder: "Application number", isActive: (f) => Boolean(f.applicationNo) },
  { key: "clientName", label: "Client Name", control: "text", valueKey: "clientName", placeholder: "Client name", isActive: (f) => Boolean(f.clientName) },
  { key: "clientEmail", label: "Client Email", control: "text", valueKey: "clientEmail", placeholder: "name@client.com", isActive: (f) => Boolean(f.clientEmail) },
  { key: "type", label: "Type", isActive: (f) => Boolean(f.type) },
  { key: "systemTag", label: "System Tag", isActive: (f) => Boolean(f.systemTag) },
  { key: "brokerTags", label: "Broker Tags", isActive: (f) => Boolean(f.brokerTags) },
  { key: "createdRange", label: "Created Date", isActive: (f) => Boolean(f.createdFrom || f.createdTo), clearKeys: ["createdFrom", "createdTo"] },
];

const FILTER_KEYS = Object.keys(DEFAULT_FILTERS);

const SORT_OPTIONS = [
  { value: "updatedAt:desc", label: "Recently updated" },
  { value: "updatedAt:asc", label: "Oldest updated" },
  { value: "createdAt:desc", label: "Newest created" },
  { value: "createdAt:asc", label: "Oldest created" },
  { value: "applicationNo:asc", label: "Application number A-Z" },
  { value: "brokerReferenceId:asc", label: "Broker reference A-Z" },
  { value: "customerName:asc", label: "Client A-Z" },
];


const readFilters = (search) => {
  const params = new URLSearchParams(search);
  return FILTER_KEYS.reduce((acc, key) => {
    acc[key] = params.get(key) || DEFAULT_FILTERS[key];
    return acc;
  }, {});
};

const readPositiveInt = (params, key, fallback) => {
  const value = Number(params.get(key));
  return Number.isInteger(value) && value > 0 ? value : fallback;
};

const readSort = (search) => {
  const params = new URLSearchParams(search);
  return {
    sortBy: params.get("sortBy") || DEFAULT_SORT.sortBy,
    sortAsc: params.get("sortAsc") === "true",
  };
};

const areShallowEqual = (first, second) => {
  const keys = new Set([...Object.keys(first || {}), ...Object.keys(second || {})]);
  for (const key of keys) {
    if (first?.[key] !== second?.[key]) return false;
  }
  return true;
};

const toCsvArray = (value) => String(value || "").split(",").filter(Boolean);
const inFilter = (values) => `in[${values.join(",")}]`;
const notInFilter = (values) => `notin[${values.join(",")}]`;
// KYC Due only covers live applications — exclude terminal/closed states:
// Onboarded (6), Declined (8), Archived (9). Rejected (4) is kept (a rejected KYC
// can still need a redo). Sent as a `status` notIn in the query; the backend
// applies it via notInFilters (no controller-specific handling needed).
const KYC_DUE_EXCLUDED_STATUSES = ["6", "8", "9"];
const STATUS_FILTER_VALUES = {
  INCOMPLETE: ["0", "2"],
  SUBMITTED: ["1", "3", "5", "7"],
  ONBOARDED: ["6", "9"],
  REJECTED: ["4"],
  DECLINED: ["8"],
};
const toStatusValues = (value) => toCsvArray(value).flatMap((item) => {
  const key = String(item || "").toUpperCase();
  return STATUS_FILTER_VALUES[key] || [item];
});
const toStartOfDay = (value) => value ? Math.floor(new Date(`${value}T00:00:00`).getTime() / 1000) : null;
const toEndOfDay = (value) => value ? Math.floor(new Date(`${value}T23:59:59`).getTime() / 1000) : null;

const addDateRange = (params, field, from, to) => {
  const fromTs = toStartOfDay(from);
  const toTs = toEndOfDay(to);
  if (fromTs && toTs) params[field] = `btw[${fromTs},${toTs}]`;
  else if (fromTs) params[field] = `gt[${fromTs}]`;
  else if (toTs) params[field] = `lt[${toTs}]`;
};

const caseIgnore = (value) => `caseIgnore[${String(value).trim()}]`;

const toApiParams = (filters, page, rowsPerPage, sort) => {
  const params = { page, rows: rowsPerPage, ...sort };
  // Status accepts simplified buckets (INCOMPLETE/SUBMITTED/…) or raw codes
  // (e.g. dashboard deep-links pass `status=2,7`).
  // "KYC_DUE" is a status-dropdown option that isn't an application status — it means
  // the KYC Verification step is Draft/Incomplete, so it routes to the kycStepStatus
  // filter instead of `status`. Any real status buckets selected alongside it still map
  // to `status` codes as usual.
  const statusTokens = filters.status ? toCsvArray(filters.status) : [];
  const kycDueViaStatus = statusTokens.includes("KYC_DUE");
  const statusValues = statusTokens.length
    ? toStatusValues(statusTokens.filter((token) => token !== "KYC_DUE").join(","))
    : toCsvArray(filters.statusCodes);
  if (statusValues?.length) params.status = inFilter(statusValues);
  if (filters.kycStatus) params.kycStatus = inFilter(toCsvArray(filters.kycStatus));
  // KYC Verification step status (e.g. "0,2") — backend matches via steps.$elemMatch.
  const kycStepStatus = filters.kycStepStatus || (kycDueViaStatus ? "0,2" : "");
  if (kycStepStatus) params.kycStepStatus = kycStepStatus;
  // Scope KYC Due to live applications via a status notIn in the query (backend
  // notInFilters). Skipped if the broker also picked explicit status buckets.
  if (kycDueViaStatus && !statusValues?.length) {
    params.status = notInFilter(KYC_DUE_EXCLUDED_STATUSES);
  }
  // System tags (Missing Info / KYC Failed) are FV Bank-stamped tags on the
  // application; resolve each selected key to its tag_id and filter on tags.tag_id
  // (the backend `tag_ids` param). Unresolved keys (registry not yet loaded) are
  // skipped rather than sent as raw keys.
  if (filters.systemTag) {
    const systemTagIds = toCsvArray(filters.systemTag).map((key) => getBrokerSystemTagId(key)).filter(Boolean);
    if (systemTagIds.length) params.tag_ids = systemTagIds.join(",");
  }
  if (filters.type) {
    // Business families are the numeric `type` sub-codes of formType BUSINESS
    // (v2); Individual Plus and other non-numeric tokens are formType values.
    const typeTokens = toCsvArray(filters.type);
    const numericTypes = typeTokens.filter((token) => /^\d+$/.test(token));
    const formTypes = typeTokens.filter((token) => !/^\d+$/.test(token));
    if (numericTypes.length) params.type = inFilter(numericTypes);
    if (formTypes.length) params.formType = inFilter(formTypes);
  }
  if (filters.clientRef) params.brokerReferenceId = caseIgnore(filters.clientRef);
  if (filters.applicationNo) params.applicationNo = caseIgnore(filters.applicationNo);
  if (filters.clientName) params.customerName = caseIgnore(filters.clientName);
  if (filters.clientEmail) params.email = caseIgnore(filters.clientEmail);
  if (filters.brokerTags) {
    // Broker Tags filter now stores a CSV of tag_ids (from the dropdown).
    const tagIds = toCsvArray(filters.brokerTags);
    if (tagIds.length) params.brokerTag_ids = tagIds.join(",");
  }
  addDateRange(params, "createdAt", filters.createdFrom, filters.createdTo);
  return params;
};

const normalizeId = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    return String(value.$oid || value.oid || value.id || value._id || "").trim();
  }
  return "";
};

const getApplicationId = (row = {}) => (
  [
    row.applicationFormId,
    row.application_form_id,
    row.applicationId,
    row.application_id,
    row.formId,
    row.form_id,
    row.application?._id,
    row.application?.id,
    row.applicationForm?._id,
    row.applicationForm?.id,
    row._id,
    row.id,
  ].map(normalizeId).find(Boolean) || ""
);

const getErrorMessage = (error, fallback = "Something went wrong.") => {
  if (typeof error === "string" && error.trim()) return error;
  if (typeof error?.message === "string" && error.message.trim()) return error.message;
  return fallback;
};

// "DD MMM YYYY HH:MM" — e.g. "10 Jun 2026 17:51".
const formatDetailDateTime = (value) => {
  if (!value) return "";
  const timestamp = Number(value);
  const date = Number.isFinite(timestamp) ? new Date(timestamp * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const datePart = date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} ${timePart}`;
};


const getBrokerApplicationRowTestId = (row = {}, index = 0) => (
  row.brokerReferenceId ||
  row.applicationNo ||
  row.email ||
  row._id ||
  `row-${index + 1}`
);

const sortToValue = (sort) => `${sort.sortBy}:${sort.sortAsc ? "asc" : "desc"}`;

const valueToSort = (value) => {
  const [sortBy, direction] = String(value || "").split(":");
  return {
    sortBy: sortBy || DEFAULT_SORT.sortBy,
    sortAsc: direction === "asc",
  };
};

const BrokerApplications = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const brokerBasePath = useBrokerBasePath();
  // When the URL carries an application id, the full detail opens in a right drawer
  // over the grid (no separate page). Closing returns to the grid (back url).
  const { applicationId: routeApplicationId } = useParams();
  const { showError } = useContext(DialogContext) || {};
  const initialParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [filters, setFilters] = useState(() => readFilters(location.search));
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => readPositiveInt(initialParams, "page", 1));
  const [rowsPerPage, setRowsPerPage] = useState(() => readPositiveInt(initialParams, "rows", DEFAULT_ROWS_PER_PAGE));
  const [sort, setSort] = useState(() => readSort(location.search));
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  // Bumped when the system-tag registry loads, to re-render row tag chips.
  const [, setSystemTagRegistryVersion] = useState(0);
  const [systemTagsLoading, setSystemTagsLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextFilters = readFilters(location.search);
    const nextPage = readPositiveInt(params, "page", 1);
    const nextRowsPerPage = readPositiveInt(params, "rows", DEFAULT_ROWS_PER_PAGE);
    const nextSort = readSort(location.search);

    setFilters((current) => areShallowEqual(current, nextFilters) ? current : nextFilters);
    setPage((current) => current === nextPage ? current : nextPage);
    setRowsPerPage((current) => current === nextRowsPerPage ? current : nextRowsPerPage);
    setSort((current) => areShallowEqual(current, nextSort) ? current : nextSort);
  }, [location.search]);

  useEffect(() => {
    let mounted = true;
    brokerTagStore.fetch().catch(() => {});
    setSystemTagsLoading(true);
    BrokerService.applicationSystemTagElements()
      .then((response) => {
        recordBrokerVisibleSystemTagElements(response?.data?.rows || []);
        if (mounted) setSystemTagRegistryVersion((current) => current + 1);
      })
      .catch(() => {})
      .finally(() => { if (mounted) setSystemTagsLoading(false); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
    if (page !== 1) params.set("page", String(page));
    if (rowsPerPage !== DEFAULT_ROWS_PER_PAGE) params.set("rows", String(rowsPerPage));
    if (sort.sortBy !== DEFAULT_SORT.sortBy) params.set("sortBy", sort.sortBy);
    if (sort.sortAsc !== DEFAULT_SORT.sortAsc) params.set("sortAsc", String(sort.sortAsc));

    const nextSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    if (nextSearch !== currentSearch) {
      const pathname = routeApplicationId
        ? buildBrokerPath(brokerBasePath, `/applications/${routeApplicationId}`)
        : buildBrokerPath(brokerBasePath, "/applications");
      navigate({ pathname, search: nextSearch }, { replace: true });
    }
  }, [filters, location.search, navigate, page, brokerBasePath, routeApplicationId, rowsPerPage, sort]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    // A ?systemTag= deep-link needs the system-tag registry to resolve its tag_id.
    // Hold the fetch (keep the skeleton) until the registry has loaded, so we never
    // briefly show unfiltered results and then re-filter.
    if (filters.systemTag && systemTagsLoading) {
      return () => { mounted = false; };
    }
    setListError("");
    BrokerService.applications(toApiParams(filters, page, rowsPerPage, sort))
      .then((response) => {
        if (!mounted) return;
        const nextRows = response?.data?.rows || [];
        setRows(nextRows);
        setTotal(Number(response?.data?.total || 0));
        // Feed the tag-suggestions store from tags already in use.
        brokerTagStore.recordFromRows(nextRows);
      })
      .catch((error) => {
        const message = getErrorMessage(error, "Unable to load applications.");
        if (!mounted) return;
        setRows([]);
        setTotal(0);
        setListError(message);
        showError?.(error || message);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [filters, page, refreshKey, rowsPerPage, showError, sort, systemTagsLoading]);

  const refreshGrid = useCallback(() => setRefreshKey((current) => current + 1), []);

  // Open the detail drawer by navigating to /applications/<id> (keeps grid filters
  // in the query string so closing restores the same grid view).
  const openDetail = useCallback((row) => {
    const applicationId = getApplicationId(row);
    if (!applicationId) return;
    navigate({ pathname: buildBrokerPath(brokerBasePath, `/applications/${applicationId}`), search: location.search });
  }, [navigate, brokerBasePath, location.search]);

  const closeDetail = useCallback(() => {
    navigate({ pathname: buildBrokerPath(brokerBasePath, "/applications"), search: location.search });
  }, [navigate, brokerBasePath, location.search]);

  const sortValue = sortToValue(sort);

  return (
    <div className="broker-page broker-applications-page broker-applications-page--booking">
      <section className="broker-grid-panel">
        <FilterBar
          filters={filters}
          onChange={(next) => {
            setPage(1);
            setFilters(next.status !== filters.status ? { ...next, statusCodes: "" } : next);
            // Search must always re-fetch — even when the draft is unchanged the
            // submitted object is reference-equal to `filters`, so setFilters
            // alone would no-op. refreshKey guarantees the fetch effect runs.
            refreshGrid();
          }}
          onClear={() => {
            setPage(1);
            setFilters({ ...DEFAULT_FILTERS });
          }}
          onRefresh={refreshGrid}
          refreshing={loading}
          statusOptions={BROKER_STATUS_OPTIONS}
          optionalFilters={BROKER_FILTER_DEFINITIONS}
          tagSuggestions={brokerTagStore.options}
          tagsLoading={brokerTagStore.loading}
          systemTagOptions={BROKER_SYSTEM_TAG_FILTER_OPTIONS}
          systemTagsLoading={systemTagsLoading}
          onNewApplication={() => navigate(buildBrokerPath(brokerBasePath, "/applications/new"))}
          sortValue={sortValue}
          sortOptions={SORT_OPTIONS}
          onSortChange={(value) => {
            setPage(1);
            setSort(valueToSort(value));
          }}
        />

        {listError ? (
          <BrokerAlert type="error" title="Applications could not be loaded" actionLabel="Retry" onAction={refreshGrid}>
            {listError}
          </BrokerAlert>
        ) : null}

        <div className="broker-applications-table" data-testid="broker-applications-grid">
          {loading && rows.length ? (
            <div className="broker-applications-table__loading" role="status" aria-live="polite">
              <FaSpinner className="broker-spin" aria-hidden="true" />
              <span>Refreshing</span>
            </div>
          ) : null}

          {!rows.length && loading ? (
            <div className="broker-applications-table__skeleton">
              <BrokerSkeleton rows={7} variant="table" />
            </div>
          ) : null}

          {!rows.length && !loading ? (
            <div className="broker-applications-table__empty">
              No client applications found.
            </div>
          ) : null}

          {rows.length ? (
            <div className="broker-applications-table__scroll">
              <table>
                <thead>
                  <tr>
                    <th>Client Ref</th>
                    <th>Application No.</th>
                    <th>Client</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th className="broker-applications-table__action-header">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const applicationId = getApplicationId(row);
                    const rowTestId = getBrokerApplicationRowTestId(row, index);
                    const clientName = row.customerName || row.email || "Draft client";
                    const systemTags = getBrokerVisibleSystemTags(row.tags);
                    const brokerTags = normalizeTags(row.brokerTags).map((tag) => brokerTagStore.byId[String(tag.tag_id)] || tag);
                    return (
                      <tr key={applicationId || rowTestId} data-testid={`broker-applications-grid-row-${rowTestId}`} data-record-id={applicationId || undefined}>
                        <td>
                          <button type="button" className="broker-link-button" onClick={() => openDetail(row)}>
                            {row.brokerReferenceId || row.applicationNo || applicationId || "Open"}
                          </button>
                        </td>
                        <td>{row.applicationNo || "--"}</td>
                        <td>
                          <div className="broker-grid-client">
                            <span>{clientName}</span>
                            {row.email ? <small>{row.email}</small> : null}
                            {systemTags.length || brokerTags.length ? (
                              <div className="broker-grid-client__tags" aria-label="Application tags">
                                {systemTags.map((tag) => (
                                  <span
                                    key={`system-${tag.tag_id || tag.details}`}
                                    className="broker-system-tag broker-system-tag--grid"
                                    title={`Flagged by FV Bank: ${tag.details}`}
                                    style={{ color: tag.color, backgroundColor: `${tag.color}1a` }}
                                  >
                                    {tag.details}
                                  </span>
                                ))}
                                {brokerTags.map((tag) => {
                                  const color = colorForTag(tag);
                                  return (
                                    <span
                                      key={`broker-${tag.tag_id}`}
                                      className="broker-grid-tag"
                                      style={{ color, backgroundColor: `${color}1a` }}
                                    >
                                      {tag.details || tag.tag_id}
                                    </span>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        </td>
                        <td>{getFormTypeLabel(row.formType, row.type)}</td>
                        <td><StatusBadge status={row.status} bucket /></td>
                        <td>
                          <div className="broker-grid-created">
                            <span className="broker-grid-created__date">{formatDetailDateTime(row.createdAt) || "--"}</span>
                            {row.createdBy ? (
                              <span className="broker-grid-created__by">
                                <BrokerAdminLabel id={row.createdBy} />
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td>{formatDetailDateTime(row.updatedAt) || "--"}</td>
                        <td>
                          <div className="broker-grid-actions">
                            <button
                              type="button"
                              className="broker-grid-action"
                              onClick={() => openDetail(row)}
                              title="Open application preview"
                              aria-label="Open application preview"
                            >
                              <FaExternalLinkAlt aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          {rows.length || total ? (
            <div className="rw-data-grid__pagination broker-applications-table__pagination" data-testid="broker-applications-grid-pagination">
              <Pagination
                page={page}
                rowsPerPage={rowsPerPage}
                total={total}
                onChange={(nextPage, nextRowsPerPage) => {
                  setPage(Number(nextPage));
                  setRowsPerPage(Number(nextRowsPerPage));
                }}
                testId="broker-applications-grid-pagination"
              />
            </div>
          ) : null}
        </div>
      </section>

      <ModalComponent
        openFrom="right"
        position="centered"
        size="medium"
        isOpen={Boolean(routeApplicationId)}
        onToggle={closeDetail}
        modalCloseButton={false}
        modalSizeStyle={{ width: "min(1200px, calc(100vw - 32px))", maxWidth: "1200px", height: "100dvh", maxHeight: "100dvh", top: 0, bottom: 0, borderRadius: 0 }}
      >
        {routeApplicationId ? (
          <div className="broker-detail-drawer">
            <BrokerApplicationDetail key={routeApplicationId} embedded onClose={closeDetail} />
          </div>
        ) : null}
      </ModalComponent>
    </div>
  );
};

export default observer(BrokerApplications);
